import { Rndm } from './Rndm.js';

import { XY } from './V2.js';

export interface scrn_type {
    id: string;
    type: string;
    dom: HTMLElement;
    fullscreen: boolean;
    focus: boolean,
    res: number;
    lock: string;
    width: number;
    height: number;
    center: XY;
    x: number;
    y: number;
    xx: number;
    yy: number;
    format: string;
    screen: number[];
    aspect: number;
    client: {
        width: number;
        height: number;
        center: XY;
        hz: any;
        browser: string;
        os: string;
        touch: boolean;
    };
    to_screen: (coordinates: XY) => XY;
    to_twrk: (coordinates: XY) => XY;
    set_width: (res: number) => void;
    set_height: (res: number) => void;
    go_fullscreen: () => void;
    resize: () => void;
    //subscribe to resize (browser resize + set_width/set_height/go_fullscreen); returns an unsubscribe fn. Fired AFTER the new size is computed.
    on_resize: (fn: () => void) => () => void;
    [key: string]: any;
}

export interface pointer_type {
    id: string;
    pos: XY;
    down: boolean;
    hold: boolean;
    up: boolean;
    leave: boolean;
    enter: boolean;
}

export function outside_screen(Screen: scrn_type, element = { x: 0, y: 0, height: 0, width: 0 }, padding = 0): XY { //touching sides
    const over = { x: 0, y: 0, out: false };
    if (element.x - element.width < Screen.x + padding) {
        over.x = element.x - element.width - Screen.x - padding;
        over.out = true;
    }
    if (element.x + element.width > Screen.xx - padding) {
        over.x = element.x + element.width - Screen.xx + padding;
        over.out = true;
    }
    if (element.y - element.height < Screen.y + padding) {
        over.y = element.y - element.height - Screen.y - padding;
        over.out = true;
    }
    if (element.y + element.height > Screen.yy - padding) {
        over.y = element.y + element.height - Screen.yy + padding;
        over.out = true;
    }
    return over; //gibt zurück ob und wie weit ausserhalb {x: 0.00164, y: 0.00344, out: true}
}

function check_os(): string {
    const ua = navigator.userAgent;
    let os = 'unknown';
    if (/iPad|iPhone|iPod/.test(ua) && !(window as any).MSStream) {
        os = 'ios';
    } else if (/Mac/.test(ua)) {
        os = 'mac';
    } else if (/Android/.test(ua)) {
        os = 'android';
    } else if (/Windows/.test(ua)) {
        os = 'windows';
    }
    return os;
}

function check_browser(): string {
    const ua = navigator.userAgent;
    let browser = 'unknown';
    if (/Safari/.test(ua) && !/Chrome/.test(ua) && !/Chromium/.test(ua)) {
        browser = 'safari';
    } else if (/Chrome/.test(ua) && !/Chromium/.test(ua)) {
        browser = 'chrome';
    } else if (/Firefox/.test(ua)) {
        browser = 'firefox';
    } else if (/Edg/.test(ua)) {
        browser = 'edge';
    }
    return browser;
}

function check_touch(): boolean {
    return ("ontouchstart" in document.documentElement);
}

export function make_screen(fullscreen: boolean = false): scrn_type {

    //resize subscribers, fired at the end of resize() once the new size is computed.
    const resize_listeners: Array<() => void> = [];

    const screen = {
        id: Rndm.id(),
        type: "scrn",
        width: 2,
        height: 2,

        dom: document.createElement("stage"),

        fullscreen: fullscreen,
        focus: true,

        res: 1,
        lock: "width", //means this direction is size
        center: { x: 0, y: 0 },
        x: -1,
        y: -1,
        xx: 1,
        yy: 1,
        format: "vertical",
        screen: [0, 0], //für webgl uniform
        aspect: 1, // (width / height)

        client: { // browser window (screen pixel res)
            hz: (window.screen as any).refreshRate || 60,
            browser: check_browser(),
            os: check_os(),
            touch: check_touch(),
            width: window.innerWidth,
            height: window.innerHeight,
            center: { x: 0, y: 0 }
        },

        to_screen: function (coordinates: XY): XY { //take twrk xy and convert it into browser space
            return { x: coordinates.x * screen.res + screen.client.center.x, y: coordinates.y * screen.res + screen.client.center.y };
        },
        to_twrk: function (coordinates: XY): XY {
            const x = (coordinates.x - this.client.center.x) / this.res;
            const y = (coordinates.y - this.client.center.y) / this.res;
            const z = coordinates.z || 0;
            return { x, y, z };
        },
        set_width: function (res = 2): void {
            this.width = res;
            this.lock = "width";
            this.resize();
        },
        set_height: function (res = 2): void {
            this.height = res;
            this.lock = "height";
            this.resize();
        },
        resize: function () {

            //update client dimensions
            this.client.width = window.innerWidth;
            this.client.height = window.innerHeight;
            this.client.center.x = this.client.width * 0.5;
            this.client.center.y = this.client.height * 0.5;

            //update res and size
            const accuracy = 1000;
            if (this.lock == "width") {
                this.res = Math.floor(this.client.width / this.width * accuracy) / accuracy;
                this.height = Math.ceil(this.client.height / this.res * accuracy) / accuracy;
            } else {
                this.res = Math.floor(this.client.height / this.height * accuracy) / accuracy;
                this.width = Math.ceil(this.client.width / this.res * accuracy) / accuracy;
            }

            //update sides and center
            this.center.x = this.width * 0.5;
            this.center.y = this.height * 0.5;
            this.x = -this.center.x;
            this.y = -this.center.y;
            this.xx = this.center.x;
            this.yy = this.center.y;

            //format
            this.format = (this.width < this.height) ? "vertical" : "horizontal";

            //webgl
            this.aspect = this.lock == "width" ? (this.width / this.height) : (this.height / this.width);
            this.screen[0] = 2 / this.width;
            this.screen[1] = -2 / this.height;

            //re-center dom
            if (this.dom) this.dom.style.transform = "translate(" + this.client.center.x + "px, " + this.client.center.y + "px)";

            //notify subscribers now the new size is in place.
            for (const fn of resize_listeners) fn();

            //console.log("SCREEN", screen.res, window.innerWidth, this);
        },
        on_resize: function (fn: () => void): () => void {
            resize_listeners.push(fn);
            return () => { const i = resize_listeners.indexOf(fn); if (i !== -1) resize_listeners.splice(i, 1); };
        },
        go_fullscreen: function () {
            if (window.screenTop && window.screenY) {
                document.getElementsByTagName('html')[0].requestFullscreen();
                screen.resize();
                console.log("fullscreen: on");
            }
        },
        input: {
            pointers: [] as pointer_type[],
            keys: new Set(),
            wheel: { x: 0, y: 0, z: 0 } as XY,
            update: function (): void {
                //emit update
            },

        }
    };

    screen.dom.id = "stage";
    document.body.appendChild(screen.dom);

    // focus
    window.addEventListener("focus", function () {
        screen.focus = true;
    });

    // blur
    window.addEventListener("blur", function () {
        screen.focus = false;
    });

    // resize
    window.addEventListener('resize', () => { screen.resize(); });

    screen.resize();

    return screen;
};